import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { DollarSign, ShoppingBag, Receipt, Loader2, Store } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";

function Kpi({ icon: Icon, tone, label, value, sub, testid }) {
  return (
    <div className="card-soft rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className="font-display font-extrabold text-2xl sm:text-3xl mt-3">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

const STATUS_TONE = {
  completed: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  processing: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "on-hold": "bg-amber-500/15 text-amber-700 border-amber-500/30",
  cancelled: "bg-rose-500/15 text-rose-700 border-rose-500/30",
  refunded: "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

export const WooMetricsPanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = month ? { month } : { days };
    const attempt = async (n) => {
      try {
        const r = await api.get("/admin/woo-metrics", { params });
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch (e) {
        if (cancelled) return;
        if (n < 3) { setTimeout(() => attempt(n + 1), 2000); return; }
        setData((prev) => (prev && !prev.error ? prev : { error: true }));
        setLoading(false);
      }
    };
    attempt(1);
    return () => { cancelled = true; };
  }, [refreshing, days, month]);

  const MONTHS = [];
  {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      MONTHS.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("default", { month: "short", year: "numeric" }) });
    }
  }

  const RangeToggle = () => (
    <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="woo-range-controls">
      <div className="flex gap-1 bg-white border border-border rounded-xl p-1" data-testid="woo-range-toggle">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => { setMonth(null); setDays(d); }} data-testid={`woo-range-${d}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!month && days === d ? "btn-gold" : "text-muted-foreground hover:text-foreground"}`}>
            {d}d
          </button>
        ))}
      </div>
      <select value={month || ""} onChange={(e) => setMonth(e.target.value || null)} data-testid="woo-month-select"
        className={`h-9 px-3 rounded-xl text-xs font-semibold border cursor-pointer outline-none ${month ? "btn-gold border-transparent" : "bg-white border-border text-foreground"}`}>
        <option value="">By month…</option>
        {MONTHS.map((m) => <option key={m.value} value={m.value} className="text-foreground bg-white">{m.label}</option>)}
      </select>
    </div>
  );

  if (loading) return (
    <div data-testid="woo-metrics-panel">
      <div className="flex items-center justify-between mb-5"><h2 className="font-display font-bold text-lg">Store performance</h2><RangeToggle /></div>
      <div className="card-soft rounded-2xl p-12 flex items-center justify-center gap-3 text-muted-foreground" data-testid="woo-metrics-loading"><Loader2 className="w-5 h-5 animate-spin" /> Pulling live revenue from WooCommerce…</div>
    </div>
  );
  if (!data || data.configured === false) return <div className="card-soft rounded-2xl p-8 text-muted-foreground" data-testid="woo-metrics-unconfigured">WooCommerce is not connected.</div>;
  if (data.error) return (
    <div data-testid="woo-metrics-panel">
      <div className="flex items-center justify-between mb-5"><h2 className="font-display font-bold text-lg">Store performance</h2><RangeToggle /></div>
      <div className="card-soft rounded-2xl p-8 text-muted-foreground" data-testid="woo-metrics-error">Couldn't reach WooCommerce right now. Try again in a moment.</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="woo-metrics-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg">Store performance</h2>
          <p className="text-xs text-muted-foreground" data-testid="woo-window-label">{data.window_label}</p>
        </div>
        <RangeToggle />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Kpi testid="woo-kpi-revenue" icon={DollarSign} tone="bg-emerald-500/15 text-emerald-600" label="Revenue" value={formatMoney(data.revenue)} sub="Processing + completed + on-hold" />
        <Kpi testid="woo-kpi-orders" icon={ShoppingBag} tone="bg-blue-500/15 text-blue-600" label="Paid orders" value={data.orders} sub={`${data.total_orders_seen} total incl. all statuses`} />
        <Kpi testid="woo-kpi-aov" icon={Receipt} tone="bg-amber-500/15 text-amber-600" label="Avg order value" value={formatMoney(data.aov)} />
      </div>

      <div className="card-soft rounded-2xl p-6">
        <h3 className="font-display font-bold mb-4">Revenue — {data.window_label}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.revenue_trend || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="date" stroke="#9c9488" fontSize={11} />
            <YAxis stroke="#9c9488" fontSize={11} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ background: "#fff", border: "1px solid #e2dcd0", borderRadius: 12 }} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="revenue" fill="#c59b27" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card-soft rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-border flex items-center gap-2">
          <Store className="w-4 h-4" style={{ color: "var(--gold)" }} />
          <h3 className="font-display font-bold text-lg">Recent WooCommerce orders</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="woo-recent-orders">
            <thead><tr className="text-left text-muted-foreground text-xs uppercase tracking-wider border-b border-border bg-[#f6f1e6]">
              <th className="px-6 py-3 font-bold">Order</th><th className="px-6 py-3 font-bold">Customer</th>
              <th className="px-6 py-3 font-bold hidden sm:table-cell">Items</th><th className="px-6 py-3 font-bold">Status</th><th className="px-6 py-3 font-bold">Total</th>
            </tr></thead>
            <tbody>
              {(data.recent_orders || []).map((o) => (
                <tr key={o.number} data-testid={`woo-order-${o.number}`} className="border-b border-border last:border-0">
                  <td className="px-6 py-4 font-semibold">#{o.number}</td>
                  <td className="px-6 py-4"><p className="font-medium">{o.customer}</p><p className="text-xs text-muted-foreground">{o.date ? new Date(o.date).toLocaleString() : ""}</p></td>
                  <td className="px-6 py-4 hidden sm:table-cell text-muted-foreground">{o.items}</td>
                  <td className="px-6 py-4"><span className={`inline-block px-2.5 py-1 pill text-[11px] font-semibold border capitalize ${STATUS_TONE[o.status] || "bg-stone-500/15 text-stone-700 border-stone-500/30"}`}>{(o.status || "").replace(/-/g, " ")}</span></td>
                  <td className="px-6 py-4 font-display font-bold">{formatMoney(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
